import { Chain } from '@1delta/chain-registry'
import { apyToApr } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { getListsMulti } from '../../helpers'
import {
  fetchGmxRawData,
  GMX_DEFAULT_APY_PERIOD,
} from '../../../vaults/gmx/api'

/**
 * GMX GM/GLV intrinsic APRs (Arbitrum) for the global intrinsic map.
 *
 * Single-sourced through the GMX vault provider's `fetchGmxRawData` client
 * and the shared `GMX_DEFAULT_APY_PERIOD` (1d) the vault listing uses — so
 * the same GM/GLV token shows one APR on both surfaces. 1d is deliberate:
 * it is the window we record and can re-derive longer windows from.
 *
 * Emits EVERY GM / GLV token the API prices, keyed by its token-list
 * `assetGroup` (the key the lending join reads). gmxinfra serves all of them
 * — 16 GM + 2 GLV on Arbitrum today — and Dolomite lists 12 of the GM markets
 * as isolation collateral (`dGM`), whose rows read the GM's group through
 * `resolveDolomiteRowIdentity`; a fetcher pinned to four hardcoded groups
 * left the other eight at 0 %. The four legacy keys stay as a fallback for a
 * list that has not been regenerated.
 */
const GMX_CHAIN = String(Chain.ARBITRUM_ONE)

const LEGACY_KEYS: Record<string, string> = {
  '0x70d95587d40a2caf56bd97485ab3eec10bee6336':
    'GMX Market ETH/USD::GM [WETH-USDC]',
  '0x47c031236e19d024b42f8ae6780e44a573170703':
    'GMX Market BTC/USD::GM [WBTC-USDC]',
  '0xdf03eed325b82bc1d4db8b49c30ecc9e05104b96':
    'GMX Liquidity Vault [WBTC-USDC]::GLV [WBTC-USDC]',
  '0x528a5bac7e746c9a509a1f4f6df58a03d44279f9':
    'GMX Liquidity Vault [WETH-USDC]::GLV [WETH-USDC]',
}

export const gmxFetcher: YieldFetcher = {
  label: 'GMX',
  fetch: async () => {
    const [{ apy }, lists] = await Promise.all([
      fetchGmxRawData(GMX_CHAIN, GMX_DEFAULT_APY_PERIOD),
      getListsMulti([GMX_CHAIN]).catch(() => ({}) as any),
    ])
    const list: Record<string, any> = lists?.[GMX_CHAIN]?.list ?? {}

    const rData: Record<string, number> = {}
    const put = (addr: string, d: { apy: number }) => {
      const a = addr.toLowerCase()
      const apr = apyToApr(d.apy) * 100
      const group = list[a]?.assetGroup
      if (group) rData[group] = apr
      const legacy = LEGACY_KEYS[a]
      if (legacy && legacy !== group) rData[legacy] = apr
    }
    for (const [addr, d] of Object.entries(apy.markets ?? {})) put(addr, d)
    for (const [addr, d] of Object.entries(apy.glvs ?? {})) put(addr, d)

    return rData
  },
}
